/**
 * Cache Management Utilities
 * Provides cache busting and version management for critical app updates
 */

// App version for cache busting - increment when critical fixes are deployed
const APP_VERSION = "1.0.1";
const VERSION_KEY = "app_version";
const LAST_CLEAR_KEY = "last_cache_clear";

interface CacheConfig {
  forceRefresh?: boolean;
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
  reloadPage?: boolean;
}

/**
 * Checks if app version has changed and clears cache if needed
 */
export const checkAndClearCacheIfNeeded = (config: CacheConfig = {}): boolean => {
  try {
    const storedVersion = localStorage.getItem(VERSION_KEY);
    const lastClear = localStorage.getItem(LAST_CLEAR_KEY);
    const now = Date.now();
    
    // Check if version has changed or force refresh is requested
    const shouldClear = config.forceRefresh || 
                       storedVersion !== APP_VERSION ||
                       !storedVersion;
    
    if (shouldClear) {
      console.log(`Cache clearing triggered. Stored version: ${storedVersion}, Current version: ${APP_VERSION}`);
      
      // Clear different types of storage based on config
      if (config.clearLocalStorage !== false) {
        clearLocalStorageSelectively();
      }
      
      if (config.clearSessionStorage !== false) {
        sessionStorage.clear();
      }
      
      // Clear React Query cache
      clearReactQueryCache();
      
      // Update version and last clear timestamp
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      localStorage.setItem(LAST_CLEAR_KEY, now.toString());
      
      console.log("Cache cleared successfully");
      
      // Reload page if requested
      if (config.reloadPage) {
        window.location.reload();
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Error checking/clearing cache:", error);
    return false;
  }
};

/**
 * Clears localStorage while preserving critical user data
 */
const clearLocalStorageSelectively = (): void => {
  const preserveKeys = [
    VERSION_KEY,
    LAST_CLEAR_KEY,
    // Preserve all Supabase auth tokens and user preferences
    'supabase.auth.token',
    'sb-woiwzyedttbcadgdvybv-auth-token',
    'sb-doyqmjwlizoqfuabvluf-auth-token', // Project-specific auth token
    'user_preferences',
    'theme_preference'
  ];
  
  // Also preserve any keys that contain 'auth' or 'supabase'
  const allKeys = Object.keys(localStorage);
  const additionalAuthKeys = allKeys.filter(key => 
    key.includes('auth') || 
    key.includes('supabase') ||
    key.includes('sb-') ||
    key.startsWith('sb.')
  );
  
  const itemsToPreserve: { [key: string]: string | null } = {};
  
  // Combine all keys to preserve
  const allKeysToPreserve = [...preserveKeys, ...additionalAuthKeys];
  
  // Save items we want to preserve
  allKeysToPreserve.forEach(key => {
    itemsToPreserve[key] = localStorage.getItem(key);
  });
  
  console.log('Preserving auth keys:', allKeysToPreserve);
  
  // Clear all localStorage
  localStorage.clear();
  
  // Restore preserved items
  Object.entries(itemsToPreserve).forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });
};

/**
 * Clears React Query cache if available
 */
const clearReactQueryCache = (): void => {
  try {
    // This will be called from components that have access to queryClient
    if ((window as any).reactQueryClient) {
      (window as any).reactQueryClient.clear();
      console.log("React Query cache cleared");
    }
  } catch (error) {
    console.warn("Could not clear React Query cache:", error);
  }
};

/**
 * Forces a complete cache clear and page reload
 */
export const forceCacheClear = (): void => {
  checkAndClearCacheIfNeeded({
    forceRefresh: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    reloadPage: true
  });
};

/**
 * Clears timezone-related cache data specifically
 */
export const clearTimezoneCache = (): void => {
  try {
    // Clear any timezone-related cache keys
    const timezoneKeys = Object.keys(localStorage).filter(key => 
      key.includes('timezone') || 
      key.includes('booking_data') || 
      key.includes('time_slot') ||
      key.includes('access_code')
    );
    
    timezoneKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear session storage items
    const sessionKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('booking_data') || 
      key.includes('verification_completed') ||
      key.includes('time_slot')
    );
    
    sessionKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });
    
    console.log("Timezone-related cache cleared");
  } catch (error) {
    console.error("Error clearing timezone cache:", error);
  }
};

/**
 * Gets current app version
 */
export const getCurrentAppVersion = (): string => APP_VERSION;

/**
 * Checks if cache was recently cleared (within last hour)
 */
export const wasRecentlyCacheCleared = (): boolean => {
  try {
    const lastClear = localStorage.getItem(LAST_CLEAR_KEY);
    if (!lastClear) return false;
    
    const lastClearTime = parseInt(lastClear);
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    
    return lastClearTime > oneHourAgo;
  } catch (error) {
    return false;
  }
};